import { useEffect, useState } from "react";
import { toast } from "sonner";
import moment from "moment";
import { PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react";
import { getCallAgentAnalytics, getCallLogs } from "../../api/api";
import DataTable from "../../components/DataTable";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";

const formatDuration = (totalSeconds) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  return `${mins}m ${secs}s`;
};

export default function CallCenter() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [analytics, setAnalytics] = useState({ agents: [], totals: { total_calls: 0, answered_calls: 0, missed_calls: 0, total_duration_seconds: 0 } });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });

  const loadAnalytics = () => {
    getCallAgentAnalytics({ from: from || undefined, to: to || undefined })
      .then((res) => setAnalytics(res.data))
      .catch((err) => toast.error(err?.message || "Could not load call analytics."));
  };

  const loadLogs = () => {
    setLogsLoading(true);
    getCallLogs({ from: from || undefined, to: to || undefined, page, limit: 15 })
      .then((res) => {
        setLogs(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load call logs."))
      .finally(() => setLogsLoading(false));
  };

  useEffect(loadAnalytics, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadLogs, [from, to, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const answerRate = (a) => (a.total_calls ? Math.round((a.answered_calls / a.total_calls) * 100) : 0);

  const agentColumns = [
    { key: "agent_name", header: "Agent", render: (a) => a.agent_name || "Unknown" },
    { key: "total_calls", header: "Total calls" },
    { key: "answered_calls", header: "Answered", render: (a) => <Badge variant="secondary">{a.answered_calls}</Badge> },
    { key: "missed_calls", header: "Missed", render: (a) => <Badge variant="destructive">{a.missed_calls}</Badge> },
    { key: "answer_rate", header: "Answer rate", render: (a) => `${answerRate(a)}%` },
    { key: "total_duration", header: "Total talk time", render: (a) => formatDuration(a.total_duration_seconds) },
    { key: "avg_duration", header: "Avg call length", render: (a) => formatDuration(a.avg_duration_seconds) },
  ];

  const directionIcon = { inbound: PhoneIncoming, outbound: PhoneOutgoing };
  const statusVariant = { answered: "secondary", missed: "destructive", ringing: "default", ended: "outline", failed: "destructive" };

  const logColumns = [
    { key: "timestamp", header: "Time", render: (l) => moment(l.timestamp).format("D MMM, h:mm A") },
    {
      key: "direction",
      header: "Direction",
      render: (l) => {
        const Icon = directionIcon[l.direction] || PhoneMissed;
        return (
          <span className="flex items-center gap-1.5 capitalize">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {l.direction}
          </span>
        );
      },
    },
    {
      key: "patient",
      header: "Patient",
      render: (l) => (
        <div>
          <p className="font-medium">{l.patient_id?.name || l.patient_number}</p>
          <p className="text-xs text-muted-foreground">{l.patient_number}</p>
        </div>
      ),
    },
    { key: "agent", header: "Agent", render: (l) => l.agent_id?.name || "—" },
    { key: "status", header: "Status", render: (l) => <Badge variant={statusVariant[l.call_status]}>{l.call_status}</Badge> },
    { key: "duration", header: "Duration", render: (l) => (l.duration_seconds ? formatDuration(l.duration_seconds) : "—") },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Call Center</h2>
        <p className="text-sm text-muted-foreground">Agent-wise call analytics and log — powered by MCube</p>
      </div>

      <div className="flex gap-3">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Total calls</p>
            <p className="font-serif text-2xl font-semibold">{analytics.totals.total_calls}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Answered</p>
            <p className="font-serif text-2xl font-semibold">{analytics.totals.answered_calls}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Missed</p>
            <p className="font-serif text-2xl font-semibold">{analytics.totals.missed_calls}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Total talk time</p>
            <p className="font-serif text-2xl font-semibold">{formatDuration(analytics.totals.total_duration_seconds)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Agent performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable columns={agentColumns} data={analytics.agents} loading={false} emptyMessage="No calls logged yet." rowKey={(a) => a.agent_id} />
        </CardContent>
      </Card>

      <div>
        <h3 className="mb-3 font-serif text-lg font-semibold">Call log</h3>
        <DataTable
          columns={logColumns}
          data={logs}
          loading={logsLoading}
          emptyMessage="No calls logged yet."
          page={page}
          numberOfPage={meta.numberOfPage}
          onPageChange={setPage}
          rowKey={(l) => l._id}
        />
      </div>
    </div>
  );
}
